import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { captureRefFromUrl } from './utils/referral';
import { track } from './utils/track';
import { captureAttribution } from './utils/attribution';
import './styles/globals.css';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import MaintenanceBanner from './components/layout/MaintenanceBanner';

// Pages
import HomePage from './pages/HomePage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import CoursePlayerPage from './pages/CoursePlayerPage';
import CartPage from './pages/CartPage';
import MyCoursesPage from './pages/MyCoursesPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/ForgotPasswordPage';
import { AboutPage, ContactPage, HowToRenewPage, TermsPage, NotFoundPage } from './pages/AboutPage';
import AdminPage from './pages/AdminPage';
import ResourcesHubPage from './pages/resources/ResourcesHubPage';
import CeRequirementsPage from './pages/resources/CeRequirementsPage';
import KansasCeRequirementsPage from './pages/resources/KansasCeRequirementsPage';
import KansasGuidePage from './pages/resources/KansasGuidePage';
import { KANSAS_GUIDES } from './pages/resources/kansasGuides';
import ProviderComparisonPage from './pages/resources/ProviderComparisonPage';
import Rules2025Page from './pages/resources/Rules2025Page';
import NineHourRulePage from './pages/resources/NineHourRulePage';
import OnlineCePage from './pages/resources/OnlineCePage';
import SubmitCePage from './pages/resources/SubmitCePage';
import WhenExpiresPage from './pages/resources/WhenExpiresPage';
import LateRenewalPage from './pages/resources/LateRenewalPage';
import FaqPage from './pages/resources/FaqPage';
import StateHomePage from './pages/state/StateHomePage';
import StateCoursesPage from './pages/state/StateCoursesPage';
import StateHowToRenewPage from './pages/state/StateHowToRenewPage';
import StateResourcesHubPage from './pages/state/StateResourcesHubPage';
import StateComingSoonPage from './pages/state/StateComingSoonPage';
import { isTestSite } from './utils/testSite';
import { STATES } from './utils/states';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

// Silently capture a ?ref=CODE referral param on any page the visitor lands on,
// plus the first-touch traffic source (search / campaign / referral) for attribution.
function ReferralCapture() {
  const location = useLocation();
  useEffect(() => { captureRefFromUrl(location.search); }, [location.search]);
  useEffect(() => { captureAttribution(location.search, location.pathname); }, [location.search, location.pathname]);
  // Funnel step tracking (utils/track.js → Admin → Funnel): one page_view per route change.
  useEffect(() => { track('page_view'); }, [location.pathname]);
  return null;
}

function AppRoutes() {
  return (
    <>
    <ReferralCapture />
    <MaintenanceBanner />
    <Routes>
      <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/courses" element={<AppLayout><CoursesPage /></AppLayout>} />
      <Route path="/courses/:id" element={<AppLayout><CourseDetailPage /></AppLayout>} />
      <Route path="/about" element={<AppLayout><AboutPage /></AppLayout>} />
      <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
      <Route path="/how-to-renew" element={<AppLayout><HowToRenewPage /></AppLayout>} />
      <Route path="/terms" element={<AppLayout><TermsPage /></AppLayout>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-pw" element={<ResetPasswordPage />} />
      <Route path="/cart" element={<AppLayout><CartPage /></AppLayout>} />
      <Route path="/my-courses" element={<AppLayout><ProtectedRoute><MyCoursesPage /></ProtectedRoute></AppLayout>} />
      <Route path="/my-profile" element={<AppLayout><ProtectedRoute><ProfilePage /></ProtectedRoute></AppLayout>} />
      <Route path="/course/:id/learn" element={<ProtectedRoute><CoursePlayerPage /></ProtectedRoute>} />
      <Route path="/admin/*" element={<AdminRoute><AdminPage /></AdminRoute>} />

      {/* State-scoped routes — each state gets its own /{state}, /{state}/courses,
          /{state}/how-to-renew, /{state}/resources. Launched states (Arizona)
          render full pages; pre-launch states render StateComingSoonPage. */}
      {Object.values(STATES).map(s => (
        s.live ? (
          <React.Fragment key={s.slug}>
            <Route path={`/${s.slug}`} element={<AppLayout><StateHomePage /></AppLayout>} />
            <Route path={`/${s.slug}/courses`} element={<AppLayout><StateCoursesPage /></AppLayout>} />
            <Route path={`/${s.slug}/how-to-renew`} element={<AppLayout><StateHowToRenewPage /></AppLayout>} />
            <Route path={`/${s.slug}/resources`} element={<AppLayout><StateResourcesHubPage /></AppLayout>} />
          </React.Fragment>
        ) : (
          <React.Fragment key={s.slug}>
            <Route path={`/${s.slug}`} element={<AppLayout><StateComingSoonPage /></AppLayout>} />
            <Route path={`/${s.slug}/*`} element={<AppLayout><StateComingSoonPage /></AppLayout>} />
          </React.Fragment>
        )
      ))}

      {/* Resources / SEO content pages — live on production */}
      <Route path="/resources" element={<AppLayout><ResourcesHubPage /></AppLayout>} />
      <Route path="/arizona-real-estate-ce-requirements" element={<AppLayout><CeRequirementsPage /></AppLayout>} />
      <Route path="/kansas-real-estate-ce-requirements" element={<AppLayout><KansasCeRequirementsPage /></AppLayout>} />
      {KANSAS_GUIDES.map(g => (
        <Route key={g.slug} path={`/${g.slug}`} element={<AppLayout><KansasGuidePage guide={g} /></AppLayout>} />
      ))}
      <Route path="/arizona-ce-provider-comparison" element={<AppLayout><ProviderComparisonPage /></AppLayout>} />
      <Route path="/arizona-ce-2025-rule-changes" element={<AppLayout><Rules2025Page /></AppLayout>} />
      <Route path="/how-fast-can-i-finish-arizona-ce" element={<AppLayout><NineHourRulePage /></AppLayout>} />
      <Route path="/arizona-ce-online" element={<AppLayout><OnlineCePage /></AppLayout>} />
      <Route path="/how-to-submit-ce-to-adre" element={<AppLayout><SubmitCePage /></AppLayout>} />
      <Route path="/when-does-my-arizona-license-expire" element={<AppLayout><WhenExpiresPage /></AppLayout>} />
      <Route path="/arizona-license-expired-late-renewal" element={<AppLayout><LateRenewalPage /></AppLayout>} />
      <Route path="/arizona-ce-faq" element={<AppLayout><FaqPage /></AppLayout>} />
      <Route path="*" element={<AppLayout><NotFoundPage /></AppLayout>} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRoutes />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
